import styled from "@emotion/styled";
import { extent } from "d3-array";
import { scaleLinear as d3ScaleLinear } from "d3-scale";
import { line as d3line } from "d3-shape";
import { motion, useAnimation } from "framer-motion";
import { adjustHue, lighten } from "polished";
import React, { useEffect, useRef, useState } from "react";
import { useMeasure } from "react-use";

const usePrevious = <T,>(value: T) => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const Point = ({ x, y, color, value = 0 }) => {
  const controls = useAnimation();
  const prevValue = usePrevious(value);

  const [runs, setRuns] = useState(0);

  useEffect(() => {
    if (prevValue !== value) {
      setRuns((state) => state + 1);
    }
  }, [prevValue, value, controls]);

  return (
    <g transform={`translate(${x}, ${y})`}>
      <motion.circle cx={0} cy={0} fill={color} r={4} />
      {new Array(runs).fill(undefined).map((_, i) => (
        <motion.circle
          key={i}
          animate={{ r: 40, opacity: 0, strokeWidth: 2 }}
          cx={0}
          cy={0}
          initial={{
            r: 0,
            opacity: 0.2,
            strokeWidth: 13,
          }}
          stroke={color}
          transition={{
            ease: "easeOut",
            duration: 1.4,
          }}
          onAnimationComplete={() => setRuns((state) => state - 1)}
        />
      ))}
    </g>
  );
};

const SVG = styled(motion.svg)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const selectX = (_, x: number) => x;
const selectY = (y: number) => y;

interface GraphProps {
  data: number[] | any;
  scaleData: number[];
  color: string;
  width?: number;
  height?: number;
}

const Graph = ({
  data = [],
  scaleData = [],
  color,
  width,
  height,
}: GraphProps) => {
  const [path, setPath] = useState("");
  const [finalPos, setFinalPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const minMaxY = extent(scaleData, selectY).concat(...extent(data, selectY));
    const scaleX = d3ScaleLinear()
      .domain(extent(scaleData, selectX))
      .range([0, width * 0.88]);
    const scaleY = d3ScaleLinear()
      .domain(extent(minMaxY))
      .range([height - 8, 60]);

    setPath(
      d3line()
        .x((d: number, n: number) => scaleX(selectX(d, n)))
        .y((d: number) => scaleY(selectY(d)))(data)
    );

    const datum = data[data.length - 1];
    setFinalPos({ x: scaleX(data.length - 1), y: scaleY(selectY(datum)) });
  }, [data, scaleData, width, height]);

  const total = selectY(data[data.length - 1]);
  // const pathLength = useMotionValue(0)

  return path ? (
    <SVG>
      <motion.g fill="none">
        <motion.path
          d={path}
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
          width="100%"
        />
        <Point color={color} value={total} x={finalPos.x} y={finalPos.y} />
      </motion.g>
    </SVG>
  ) : null;
};

const GraphContainer = styled(motion.div)`
  position: relative;
  flex: 1;
  width: 100%;

  height: 30vh;
`;

const Graphs = ({ data, color }) => {
  const [ref, { width, height }] = useMeasure();
  return (
    <GraphContainer
      ref={ref}
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      transition={{ delay: 1 }}
    >
      {Array.isArray(data) ? (
        <Graph
          key="supply"
          color={color}
          data={data}
          height={height}
          scaleData={data}
          width={width}
        />
      ) : typeof data === "object" ? (
        <>
          {Object.keys(data).map((k) => (
            <Graph
              key={k}
              color={
                k === "reward" ? lighten(0.2, adjustHue(10, color)) : color
              }
              data={data[k]}
              height={height}
              scaleData={data.supply}
              width={width}
            />
          ))}
        </>
      ) : null}
    </GraphContainer>
  );
};

export default Graphs;
